import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { MailLinkDto } from "../../../services/mailLinkService";
import type { DocumentDto } from "../../../services/documentService";
import { labelForUserKey, type AppUser } from "../../../services/userService";
import { documentService } from "../../../services/documentService";
import { useOpenDocument } from "../../documents/useDocumentViewer";
import type { Kommentar, KommentarZiel } from "../../notizbloecke/kommentarModell";
import { zielSchluessel } from "../../notizbloecke/kommentarModell";
import type { BezugKandidat } from "../../notizbloecke/bezugKandidaten";
import type { KommentarAendern } from "../../notizbloecke/KommentarFaden";
import type { KommentarZusatz } from "../../notizbloecke/kommentarInhalt";
import type { NoteRefArt } from "../../../services/vorgangService";
import { KommentarSpalte } from "./KommentarSpalte";
import * as aufgeklappt from "./kommentarAufgeklappt";
import { FADEN_AUF_EREIGNIS } from "./sprungmarke";
import { DokumentHoverVorschau } from "../detail/DokumentHoverVorschau";
import { dokumentZugAus, mailZugAusLink, ZIEHTYP_DOKUMENT, ZIEHTYP_MAIL, ZIEHTYP_POSTFACH_MAIL } from "../detail/todoDrop";
import { MailHoverVorschau } from "../detail/MailHoverVorschau";
import { DateiTypPlakette, DateiTypSymbol } from "../../documents/DateiTypSymbol";
import { DokumentVorschaubild, zeigtVorschaubild } from "./DokumentVorschaubild";
import { dateiTypInfo } from "../../documents/dateiTyp";
import {
    IcoAnruf,
    IcoDokument,
    IcoDownload,
    IcoKalender,
    IcoLupe,
    IcoMail,
    IcoNotiz,
    IcoPapierkorb,
    IcoSprechblase,
    IcoMehrWaagerecht,
} from "../v3/vg3Icons";
import type { VorgangEntwurf } from "../../../services/graphMailService";
import { dokumenteHerunterladen, ohneDoppelte } from "./dokumenteHerunterladen";
import { filtereZeilen } from "./AlleListeDialog";
import { archivZaehlung, type ArchivArt, type ArchivZeile } from "./archivZeilen";
import "./vorgangV4.css";
import { BottomSheet } from "../../calendar/BottomSheet";
import { useSheetKlasse } from "./mobil/sheetKlasse";
import { hinweisen } from "../../redesign/bestaetigen";

/**
 * Die Zeilenliste in Archiv-Optik — für das ARCHIV am Seitenende und für den
 * VERLAUF eines Arbeitsstrangs (PO 15.09.2026).
 *
 * ══ WARUM EINE EIGENE LISTE ════════════════════════════════════════════════
 * Seit dem 15.09.2026 stehen Mail, Anruf, Dokument und Termin nicht mehr als
 * Karten zwischen den Notizen, sondern im Verlauf darunter — „der Verlauf
 * dann so wie das Archiv, bei mehreren To-dos im To-do und bei einem Vorgang
 * ohne To-do dann Archiv". Die Zeile muss damit alles können, was die Karte
 * konnte: die Hover-Vorschau (Mail formatiert, Dokument mit erster Seite), den
 * Rechtsklick (Umbenennen, Zuweisen, Entfernen) und die Sprechblase mit den
 * Kommentaren. `ListenFlaeche` kann das nicht — sie zeichnet die schlichte
 * Zeilenfassung und bleibt unverändert. (Gerendert wird sie seit dem
 * 20.09.2026 nirgends mehr: ihre Nutzer waren V5 und V6. Von hier gebraucht
 * werden nur noch `filtereZeilen` und der Typ `ListenZeile`.)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ══ HALBE KARTE, KOMMENTARE DANEBEN (PO 18.09.2026) ════════════════════════
 * „Dann zieht man das Ding bis hier und hat hier sein Kommentarfeld." Unter
 * dem Kopf steht seitdem ein Zweispalter: links die Karte, rechts der
 * Kommentarfaden (`KommentarSpalte`). Vorher stand rechts nichts, und die
 * Kommentare lagen hinter einer Sprechblase im Kopf — man sah nicht, was
 * kommentiert war, ohne zu klicken.
 *
 * Unter 900 px fällt die Teilung weg; dort stehen Karte und Kommentare
 * untereinander (`vorgangV4.css`).
 * ═══════════════════════════════════════════════════════════════════════════
 */

const STEMPEL: Record<ArchivArt, string> = {
    mail: "Mail",
    anruf: "Anruf",
    dokument: "Dokument",
    termin: "Termin",
    notiz: "Notiz",
    kommentar: "Kommentar",
};

export type ChipArt = { art: ArchivArt | "alle"; label: string };

/** Die Chips der Arten, die in `zeilen` VORKOMMEN — für den Verlauf eines Strangs. */
export function vorhandeneArten(zeilen: ArchivZeile[]): ChipArt[] {
    const n = archivZaehlung(zeilen);
    const alle: ChipArt[] = [
        { art: "mail", label: "Mails" },
        { art: "anruf", label: "Anrufe" },
        { art: "dokument", label: "Dokumente" },
        { art: "termin", label: "Termine" },
    ];
    return [{ art: "alle", label: "Alle" }, ...alle.filter((c) => n[c.art as ArchivArt] > 0)];
}

/**
 * Das Kommentarziel einer Zeile — dieselbe Regel wie am früheren Ereignisblock:
 * eine lokale Maildatei ist technisch ein Dokument.
 *
 * Der TERMIN hat seit dem 20.09.2026 einen Faden (PO: „ja"). Bis dahin fehlte
 * ihm als einzigem das Bezugsfeld an der Notiz; seine Karte stand im Verlauf,
 * ohne dass jemand etwas dazu sagen konnte.
 *
 * Die NOTIZ bleibt ohne: Ihr Faden hängt am Notizblock, nicht an der
 * Archivzeile — dieselbe Notiz stünde sonst mit zwei getrennten Fäden da.
 */
export function zielFuerZeile(z: ArchivZeile): KommentarZiel | null {
    if (z.notiz) return null;
    // Der Mail-Entwurf (User 22.10.2026) hat noch keinen MailLink — sein Faden
    // hängt an der Graph-Id und wandert beim Senden an die gesendete Mail.
    if (z.entwurf) return { art: "entwurf", id: 0, entwurfId: z.entwurf.draftId, todoId: z.todoId };
    if (z.termin) return { art: "termin", id: z.termin.terminId, todoId: z.todoId };
    const id = z.mail?.id ?? z.anruf?.id ?? z.dokument?.id;
    if (id == null) return null;
    const art = z.dokument ? "dokument" : z.anruf ? "anruf" : "mail";
    return { art, id, todoId: z.todoId };
}

export type ZeilenKommentare = {
    fadenZu: (ziel: KommentarZiel) => Kommentar[];
    users: AppUser[];
    aktuellerUserId?: string;
    onNeu?: (
        ziel: KommentarZiel,
        text: string,
        mentions: string[],
        anker?: string,
        zusatz?: KommentarZusatz,
    ) => Promise<void>;
    onAendern?: KommentarAendern;
    onLoeschen?: (k: Kommentar) => Promise<void>;
    /** Das Angebot hinter `#` im Kommentar (PO 28.09.2026). */
    kandidaten?: BezugKandidat[];
    /** Klick auf einen Chip oder Anhang im Kommentar. */
    onBezugOeffnen?: (art: NoteRefArt, id: string) => void;
    /** Anhänge im Kommentar — nur, wenn `onNeu`/`onAendern` den Zusatz auswerten. */
    mitAnhang?: boolean;
    /**
     * Den Haken setzen (PO 18.09.2026). Er hängt NICHT am Schreibrecht: abhaken
     * darf, wer den Vorgang sieht („erstmal jeder", PO 20.09.2026) — gebeten
     * wird der Erwähnte, und der ist nicht zwangsläufig Bearbeiter.
     */
    onErledigt?: (k: Kommentar, erledigt: boolean) => Promise<void>;
};

type Props = {
    zeilen: ArchivZeile[];
    arten: ChipArt[];
    /** Die Spalte „aus welchem To-do" — nur, wo es mehrere Stränge gibt. */
    mitHerkunft: boolean;
    leerHinweis: string;
    suchPlatzhalter: string;
    onOeffneMail: (link: MailLinkDto) => void;
    /** Einen Mail-Entwurf im Verfassen-Fenster weiterschreiben (User 22.10.2026). */
    onOeffneEntwurf?: (e: VorgangEntwurf) => void;
    /** Einen Mail-Entwurf löschen — fragt selbst nach. Ohne ihn kein Papierkorb. */
    onEntwurfLoeschen?: (e: VorgangEntwurf) => void;
    /** Ohne ihn ist die Termin-Zeile ein Absatz, kein Knopf. */
    onOeffneTermin?: (terminId: number) => void;
    /** Rechtsklick auf Mail, Anruf, Dokument oder Termin — nicht auf Notizen. */
    onKontextmenue?: (zeile: ArchivZeile, e: React.MouseEvent) => void;
    /** Fehlt es, trägt keine Zeile eine Sprechblase. */
    kommentare?: ZeilenKommentare;
    /**
     * Wie viele Karten am Stück stehen, bevor „weitere laden" kommt
     * (PO 18.09.2026: „weitere laden nach 5 Einträgen"). Ohne Angabe steht
     * alles da — so liest das ARCHIV, das ja gerade die vollständige Liste
     * ist. Die Zahl selbst steht an einer Stelle: {@link VERLAUF_DECKEL}.
     */
    deckel?: number;
    /**
     * Steht die Suchfläche offen? Im Strang hängt sie am Knopf „Verlauf
     * durchsuchen" in der Fußleiste (PO 18.09.2026) und wird von dort
     * gesteuert; das Archiv IST die Suchfläche und lässt sie offen.
     */
    suchOffen?: boolean;
    /** Namen zu Benutzerschlüsseln — für den Verfasser einer Notizkarte. */
    users?: AppUser[];
    /**
     * Dateiname des ZIP für „Dokumente herunterladen" (PO 28.09.2026). Ohne
     * ihn steht der Sammelknopf nicht da — einzeln lädt jede Dokumentkarte
     * über ihr eigenes Symbol trotzdem herunter.
     */
    zipName?: string;
    /**
     * Die Überschrift über der LINKEN Spalte — im Verlauf eines Strangs
     * „Verlauf" (PO 20.09.2026: „die Spaltenbenennung bei Verlauf belassen").
     * Die rechte bekommt keine: sie erklärt sich durch das, was darin steht,
     * und ein zweiter Titel wäre Beiwerk. Ohne Angabe steht keine da — das
     * Archiv trägt seine Überschrift schon im Abschnitt darüber.
     */
    spaltenTitel?: string;
    /**
     * HANDY (Plan „Vorgangsdetail mobil", PR 6): kompakte Karten — Symbol,
     * Titel, Meta-Zeile; ⋯ statt Rechtsklick (iOS kennt kein `contextmenu`
     * per langem Drücken); die Kommentare als Pille, die ein Sheet öffnet.
     */
    kompakt?: boolean;
};

/**
 * Nach fünf Karten kommt „weitere laden" (PO 18.09.2026). Fünf, weil die
 * Karten verschieden hoch sind: Bei fünf Mails mit Anriss steht der Fuß der
 * Karte noch im Bild, und wer mehr sehen will, sagt es.
 *
 * Die Zahl steht hier allein, damit sie sich später einstellen lässt — der
 * Zettel vom 18.09.2026 fragt danach („ist … einstellbar").
 */
export const VERLAUF_DECKEL = 5;

export const EreignisListeV4: React.FC<Props> = ({
    zeilen,
    arten,
    mitHerkunft,
    leerHinweis,
    suchPlatzhalter,
    onOeffneMail,
    onOeffneEntwurf,
    onEntwurfLoeschen,
    onOeffneTermin,
    onKontextmenue,
    kommentare,
    deckel,
    suchOffen = true,
    users,
    spaltenTitel,
    zipName,
    kompakt = false,
}) => {
    const [filter, setFilter] = useState<ArchivArt | "alle">("alle");
    const [suche, setSuche] = useState("");
    const [alleZeigen, setAlleZeigen] = useState(false);
    /**
     * Welche Fäden ganz dastehen. Aus dem Speicher vorbelegt (PO 20.09.2026:
     * die Spalte merkt sich das), danach lebt der Stand hier — geschlüsselt
     * nach dem KOMMENTARZIEL (`mail:7`), nicht nach der Zeile: dieselbe Mail
     * steht im Verlauf ihres To-dos und im Archiv, und beide Male ist es
     * derselbe Faden.
     */
    const [offeneFaeden, setOffeneFaeden] = useState<Set<string>>(
        () => new Set(aufgeklappt.lies()),
    );
    const fadenUmschalten = useCallback((schluessel: string, auf: boolean) => {
        setOffeneFaeden((bisher) => {
            const liste = aufgeklappt.mitStand([...bisher], schluessel, auf);
            aufgeklappt.schreibe(liste);
            return new Set(liste);
        });
    }, []);
    // Sprungmarke einer Mitteilung (User 01.11.2026): den Faden aufklappen,
    // damit der gemeinte Kommentar dasteht — ohne es sich zu merken.
    useEffect(() => {
        const auf = (e: Event) => {
            const schluessel = (e as CustomEvent<string>).detail;
            if (typeof schluessel !== "string" || !schluessel) return;
            setOffeneFaeden((bisher) => (bisher.has(schluessel) ? bisher : new Set([...bisher, schluessel])));
        };
        window.addEventListener(FADEN_AUF_EREIGNIS, auf);
        return () => window.removeEventListener(FADEN_AUF_EREIGNIS, auf);
    }, []);
    const openDocument = useOpenDocument();
    const zaehlung = useMemo(() => archivZaehlung(zeilen), [zeilen]);

    // Verschwindet die gewählte Art (letzte Mail umgehängt), fällt der Filter
    // auf „Alle" zurück — sonst stünde eine leere Liste ohne gedrückten Chip da.
    const aktiv = arten.some((a) => a.art === filter) ? filter : "alle";
    const treffer = useMemo(
        () => filtereZeilen(aktiv === "alle" ? zeilen : zeilen.filter((z) => z.art === aktiv), suche),
        [zeilen, aktiv, suche],
    );

    /*
     * WER SUCHT, WILL ALLE TREFFER SEHEN. Ein Deckel über einer Suche ließe
     * den gesuchten Eintrag hinter „weitere laden" verschwinden — und man
     * suchte weiter, obwohl er längst gefunden ist. Dasselbe gilt für einen
     * gesetzten Art-Filter: „Dokumente" heißt alle Dokumente.
     */
    const offen = deckel == null || alleZeigen || suche.trim() !== "" || aktiv !== "alle";
    const sichtbar = offen ? treffer : treffer.slice(0, deckel);
    const rest = treffer.length - sichtbar.length;

    /*
     * HERUNTERLADEN, WAS GERADE DASTEHT (PO 28.09.2026). Der Sammelknopf
     * nimmt die Dokumente der TREFFER — mit Suche und Art-Filter, aber ohne
     * den „weitere laden"-Deckel: wer „Dokumente" wählt und herunterlädt,
     * meint alle, nicht die ersten fünf.
     */
    const trefferDokumente = useMemo(
        () => ohneDoppelte(treffer.flatMap((z) => (z.dokument ? [z.dokument] : []))),
        [treffer],
    );
    const [ladeStand, setLadeStand] = useState<{ fertig: number; gesamt: number } | null>(null);
    const alleHerunterladen = useCallback(async () => {
        if (!zipName || ladeStand || trefferDokumente.length === 0) return;
        setLadeStand({ fertig: 0, gesamt: trefferDokumente.length });
        try {
            const ergebnis = await dokumenteHerunterladen(trefferDokumente, zipName, (fertig, gesamt) =>
                setLadeStand({ fertig, gesamt }),
            );
            if (ergebnis.fehlgeschlagen.length > 0) {
                void hinweisen({
                    titel: "Nicht alles heruntergeladen",
                    text:
                        `${ergebnis.geladen} von ${trefferDokumente.length} Dokumenten heruntergeladen. ` +
                        `Nicht dabei: ${ergebnis.fehlgeschlagen.join(", ")}`,
                    ton: "danger",
                });
            }
        } catch (err: any) {
            console.error("[EreignisListeV4] Herunterladen fehlgeschlagen", err);
            void hinweisen({
                titel: "Herunterladen fehlgeschlagen",
                text: err?.message ?? "Die Dokumente konnten nicht heruntergeladen werden.",
                ton: "danger",
            });
        } finally {
            setLadeStand(null);
        }
    }, [zipName, ladeStand, trefferDokumente]);

    const oeffnen = (z: ArchivZeile): (() => void) | undefined => {
        if (z.entwurf) return onOeffneEntwurf ? () => onOeffneEntwurf(z.entwurf!) : undefined;
        if (z.mail) return () => onOeffneMail(z.mail!);
        // T-220: Ein vertraulicher Termin lässt sich nicht öffnen — die Seite hätte nur „kein Zugriff“.
        if (z.termin?.vertraulich) return undefined;
        if (z.termin && onOeffneTermin) return () => onOeffneTermin(z.termin!.terminId);
        if (z.dokument) {
            return () =>
                openDocument(z.dokument!, {
                    download: (docId) => void documentService.openSignedDownload(docId),
                });
        }
        return undefined;
    };

    const oeffneGefaltet = (x: { mail?: MailLinkDto; dokument?: DocumentDto }) => {
        if (x.mail) onOeffneMail(x.mail);
        else if (x.dokument) {
            openDocument(x.dokument, {
                download: (docId) => void documentService.openSignedDownload(docId),
            });
        }
    };

    return (
        <>
            {/* Die Suchfläche steht im Archiv immer offen; im Verlauf eines
                Strangs klappt sie der Knopf „Verlauf durchsuchen" in der
                Fußleiste auf (PO 18.09.2026). `hidden` statt Weglassen: die
                getippte Suche und der gewählte Chip überleben das Zuklappen. */}
            <div className="vg4-liste-kopf" hidden={!suchOffen}>
                <label className="vg4-such-feld">
                    <span className="vg4-such-ico" aria-hidden>
                        <IcoLupe size={16} />
                    </span>
                    <input
                        className="vg4-liste-such"
                        value={suche}
                        placeholder={suchPlatzhalter}
                        aria-label={suchPlatzhalter}
                        onChange={(e) => setSuche(e.target.value)}
                    />
                </label>
                <div className="vg4-archiv-chips" role="group" aria-label="Art">
                    {arten.map(({ art, label }) => (
                        <button
                            key={art}
                            type="button"
                            className="vg4-chip"
                            aria-pressed={aktiv === art}
                            onClick={() => setFilter(art)}
                        >
                            {/* Das Zeichen der Art vor dem Wort (PO 28.09.2026:
                                „generell die Icons mit anzeigen"). */}
                            {art !== "alle" && <ArtSymbol art={art} size={14} />}
                            {label}
                            <span className="vg4-chip-n">{art === "alle" ? zeilen.length : zaehlung[art]}</span>
                        </button>
                    ))}
                </div>
                {zipName && trefferDokumente.length > 0 && (
                    <button
                        type="button"
                        className="vg4-chip vg4-dl-alle"
                        disabled={!!ladeStand}
                        title={
                            trefferDokumente.length === 1
                                ? "Das Dokument herunterladen"
                                : `Die ${trefferDokumente.length} Dokumente als ZIP herunterladen`
                        }
                        onClick={() => void alleHerunterladen()}
                    >
                        <IcoDownload size={14} />
                        {ladeStand
                            ? `Lädt… ${ladeStand.fertig}/${ladeStand.gesamt}`
                            : trefferDokumente.length === 1
                              ? "Herunterladen"
                              : "Alle herunterladen"}
                        {!ladeStand && <span className="vg4-chip-n">{trefferDokumente.length}</span>}
                    </button>
                )}
            </div>

            <div className="vg4-liste-b">
                {/* Angeschrieben ist nur die LINKE Spalte, und nur einmal über
                    der Liste — nicht an jeder Karte. */}
                {kompakt && spaltenTitel && treffer.length > 0 ? (
                    <div className="vg4m-verlauf-titel">{spaltenTitel}</div>
                ) : null}
                {spaltenTitel && treffer.length > 0 && !kompakt ? (
                    <div className="vg4-liste-titel" aria-hidden>
                        {spaltenTitel}
                    </div>
                ) : null}
                {treffer.length === 0 ? (
                    <div className="vg4-liste-leer">{suche.trim() ? "Keine Treffer." : leerHinweis}</div>
                ) : (
                    sichtbar.map((z) =>
                        kompakt ? (
                            <KarteKompakt
                                key={z.key}
                                z={z}
                                onOeffnen={oeffnen(z)}
                                onOeffneGefaltet={oeffneGefaltet}
                                {...(users ? { users } : {})}
                                {...(onKontextmenue && !z.notiz && !z.entwurf ? { onKontextmenue } : {})}
                                {...(kommentare ? { kommentare } : {})}
                                {...(z.entwurf && onEntwurfLoeschen ? { onEntwurfLoeschen } : {})}
                            />
                        ) : (
                        <Karte
                            key={z.key}
                            z={z}
                            mitHerkunft={mitHerkunft}
                            onOeffnen={oeffnen(z)}
                            onOeffneGefaltet={oeffneGefaltet}
                            offeneFaeden={offeneFaeden}
                            onFadenUmschalten={fadenUmschalten}
                            {...(users ? { users } : {})}
                            {...(onKontextmenue && !z.notiz && !z.entwurf ? { onKontextmenue } : {})}
                            {...(kommentare ? { kommentare } : {})}
                            {...(z.entwurf && onEntwurfLoeschen ? { onEntwurfLoeschen } : {})}
                        />
                        ),
                    )
                )}
                {rest > 0 && (
                    <button type="button" className="vg4-mehr-laden" onClick={() => setAlleZeigen(true)}>
                        {rest} weitere laden
                    </button>
                )}
            </div>
        </>
    );
};

/**
 * EINE KARTE IM VERLAUF (PO 18.09.2026, Artifact „Notizfläche und
 * Verlaufskarten").
 *
 *     [Mail] Klaus Baltes · Anforderung Ihrer Unterlagen    Fr., 04.09.  💬2
 *     ┌────────────────────────────────────────────┐
 *     │ KB   Sehr geehrte Damen und Herren, anbei … │
 *     └────────────────────────────────────────────┘
 *
 * Bis dahin war das eine Rasterzeile mit vier festen Spalten. Der Zettel vom
 * 18.09.2026 verlangt das Kartenbild zurück: „einfach als Liste von Karten
 * (wie wir sie mal hatten)", und die Karten „so groß wie nötig und nicht volle
 * Breite".
 *
 * Seit dem 18.09.2026 mit KOMMENTARSPALTE: Die Karte nimmt die linke Hälfte,
 * die Kommentare stehen rechts daneben.
 *
 *     [Mail] Klaus Baltes · Anforderung Ihrer Unterlagen         Fr., 04.09.
 *     ┌──────────────────────────────┐   ┌──────────────────────────────┐
 *     │ KB  Sehr geehrte Damen und … │   │ DC  @SK ruf bitte vorher an  │
 *     └──────────────────────────────┘   └──────────────────────────────┘
 *                                        2 weitere
 *                                        💬 Kommentar schreiben
 *
 * TRÄGT EIN EINTRAG NICHTS UND FÜHRT NIRGENDWOHIN — ein Anruf ohne
 * Gesprächsnotiz —, bleibt die linke Hälfte leer: ein leerer weißer Kasten ist
 * schlimmer als keiner. Die rechte steht trotzdem, denn gerade der verpasste
 * Anruf ist der, zu dem jemand etwas sagen will.
 */
const Karte: React.FC<{
    z: ArchivZeile;
    mitHerkunft: boolean;
    onOeffnen: (() => void) | undefined;
    /** Eine zusammengefaltete Mail der Unterhaltung bzw. frühere Fassung öffnen. */
    onOeffneGefaltet?: (x: { mail?: MailLinkDto; dokument?: DocumentDto }) => void;
    /** Die Schlüssel der ganz gezeigten Fäden — siehe `EreignisListeV4`. */
    offeneFaeden: Set<string>;
    onFadenUmschalten: (schluessel: string, auf: boolean) => void;
    users?: AppUser[];
    onKontextmenue?: (zeile: ArchivZeile, e: React.MouseEvent) => void;
    kommentare?: ZeilenKommentare;
    onEntwurfLoeschen?: (e: VorgangEntwurf) => void;
}> = ({ z, mitHerkunft, onOeffnen, onOeffneGefaltet, offeneFaeden, onFadenUmschalten, users, onKontextmenue, kommentare, onEntwurfLoeschen }) => {
    const ziel = kommentare ? zielFuerZeile(z) : null;
    const onNeu = kommentare?.onNeu;
    // Der Verfasser einer Notiz steht als Benutzerschlüssel in der Zeile —
    // aufgelöst wird er erst hier, wo die Benutzerliste vorliegt.
    const werRoh = z.wer || (z.autorKey ? labelForUserKey(z.autorKey, users ?? []) : "");
    // Ohne Hochladenden und ohne Kunden fällt `wer` beim Dokument auf
    // „Dokument“ zurück — direkt neben der Plakette „Dokument“ stand das dann
    // doppelt (User 22.10.2026). Die Plakette genügt.
    const wer = z.art === "dokument" && werRoh === STEMPEL.dokument ? "" : werRoh;

    const inhalt = z.auszug ? (
        z.dokument ? (
            <span className="vg4-ek-datei" title={z.auszug}>
                {z.auszug}
            </span>
        ) : (
            <span className="vg4-ek-text">{z.auszug}</span>
        )
    ) : null;

    const rumpf = (
        <>
            {/* Ein Dokument zeigt seinen Dateityp (PDF, Excel, Bild …) statt
                des immer gleichen Blatts (User 22.10.2026). Das Symbol trägt
                selbst Kürzel und Namen, darum ohne aria-hidden. */}
            {z.dokument ? (
                zeigtVorschaubild(z.dokument) ? (
                    // Ein Bild zeigt sich selbst, klein (User 22.10.2026) — das
                    // Symbol bleibt Rückfall, solange es lädt oder wenn es scheitert.
                    <span className={`vg4-ek-ico ${z.art} mit-dateityp mit-vorschaubild`}>
                        <DokumentVorschaubild
                            id={z.dokument.id}
                            fileName={z.dokument.fileName}
                            mimeType={z.dokument.mimeType}
                        />
                    </span>
                ) : (
                    <span className={`vg4-ek-ico ${z.art} mit-dateityp`}>
                        <DateiTypSymbol fileName={z.dokument.fileName} mimeType={z.dokument.mimeType} size={40} />
                    </span>
                )
            ) : (
                <span className={`vg4-ek-ico ${z.art}`} aria-hidden>
                    {z.mail ? initialen(wer) : <ArtSymbol art={z.art} />}
                </span>
            )}
            {inhalt}
        </>
    );
    // T-225: Ein Dokument lässt sich aus dem Vorgang in ein offenes
    // Mailfenster ziehen und hängt dort als KMVS-Dokument an. Eigener
    // MIME-Typ — die Datei-Ablagen des Vorgangs (`DateiDropZoneV4`) ignorieren
    // ihn, es wird also nichts versehentlich ein zweites Mal hochgeladen.
    // Seit dem 22.10.2026 auch die Mail selbst: Sie hängt dort als `.eml` an
    // (wie Outlooks „Als Anlage weiterleiten").
    const ziehen = ziehbarAls(z.dokument ? { dokument: z.dokument } : z.mail ? { mail: z.mail } : null);
    const karte = onOeffnen ? (
        <button
            type="button"
            className={`vg4-ek-karte ${z.art}`}
            title={
                z.dokument || z.mail
                    ? `${z.titel} — zum Anhängen in ein Mailfenster ziehen`
                    : z.entwurf
                      ? z.entwurf.bearbeitbar !== false
                        ? `Entwurf „${z.titel}“ weiterschreiben`
                        : `Entwurf „${z.titel}“ ansehen (nur lesen, aus ${z.entwurf.mailbox})`
                      : z.titel
            }
            onClick={onOeffnen}
            {...ziehen}
        >
            {rumpf}
        </button>
    ) : inhalt ? (
        <div className={`vg4-ek-karte ${z.art} statisch`} {...ziehen}>{rumpf}</div>
    ) : null;

    // Die Vorschau hängt an der Karte — dort steht der Inhalt, und dorthin
    // führt der Klick. Anruf, Termin und Notiz haben keine.
    const mitVorschau = !karte ? null : z.mail ? (
        <MailHoverVorschau link={z.mail} ankerClass="vg4-ek-anker">
            {karte}
        </MailHoverVorschau>
    ) : z.dokument ? (
        <DokumentHoverVorschau dokument={z.dokument} ankerClass="vg4-ek-anker">
            {karte}
        </DokumentHoverVorschau>
    ) : (
        karte
    );

    return (
        <div
            className={z.entwurf ? "vg4-ek vg4-ek-entwurf" : "vg4-ek"}
            data-ereignis-key={z.key}
            {...(z.dokument
                ? {
                      // Typfarbe für Kartenrand und Symbolkreis (vorgangV4.css).
                      style: {
                          "--dt-farbe": dateiTypInfo(z.dokument.fileName, z.dokument.mimeType).farbe,
                      } as React.CSSProperties,
                  }
                : {})}
            {...(onKontextmenue ? { onContextMenu: (e: React.MouseEvent) => onKontextmenue(z, e) } : {})}
        >
            <div className="vg4-ek-kopf">
                <span className={`vg4-stempel ${z.art}`}>
                    <ArtSymbol art={z.art} size={11} />
                    {STEMPEL[z.art]}
                </span>
                {/* Noch nicht gesendet (User 22.10.2026): sonst sieht der
                    Entwurf aus wie jede andere Mail. */}
                {z.entwurf && <span className="vg4-entwurf-marke">Entwurf</span>}
                {wer && <span className="vg4-ek-wer">{wer}</span>}
                {z.kennung && (
                    // Der Trennpunkt davor gehört zum Namen — fehlt der (Dokument
                    // ohne Urheber), hinge er sonst allein hinter der Plakette.
                    <span className={wer ? "vg4-ek-kennung" : "vg4-ek-kennung ohne-wer"} title={z.kennung}>
                        {z.kennung}
                    </span>
                )}
                {/* Woher die Karte stammt — nur im Archiv, wo Stränge
                    nebeneinanderstehen. Anders als im Raster von vorher fällt
                    sie hier einfach weg, wenn es nichts zu sagen gibt. */}
                {mitHerkunft && z.todoTitel && (
                    <span className="vg4-ek-herkunft" title={`Aus dem To-do „${z.todoTitel}“`}>
                        {z.todoTitel}
                    </span>
                )}
                {/* Ein Dokument, das dieser Vorgang nur ZUSÄTZLICH anzeigt
                    (PO 22.09.2026). Ohne den Hinweis sähe es aus wie jedes
                    andere — und „aus dem Vorgang entfernen" wirkte, als nähme
                    es die Datei mit. */}
                {z.dokument?.gast && (
                    <span
                        className="vg4-ek-herkunft"
                        title={
                            z.dokument.gast.heimat
                                ? `Dieses Dokument ist in Vorgang ${z.dokument.gast.heimat.vorgangNbr ?? z.dokument.gast.heimat.id} zu Hause und wird hier zusätzlich angezeigt.`
                                : "Dieses Dokument liegt in der Kundenakte und wird hier zusätzlich angezeigt."
                        }
                    >
                        {z.dokument.gast.heimat
                            ? `aus Vorgang ${z.dokument.gast.heimat.vorgangNbr ?? z.dokument.gast.heimat.id}`
                            : "aus der Kundenakte"}
                    </span>
                )}
                <span className="vg4-ek-wann">{z.wann}</span>
                {z.entwurf && z.entwurf.bearbeitbar !== false && onEntwurfLoeschen && (
                    <button
                        type="button"
                        className="vg4-ek-dl"
                        title="Entwurf löschen"
                        aria-label={`Entwurf „${z.titel}“ löschen`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onEntwurfLoeschen(z.entwurf!);
                        }}
                    >
                        <IcoPapierkorb size={15} />
                    </button>
                )}
                {/* Herunterladen (PO 28.09.2026) — ohne Umweg über den
                    Betrachter und auch für Leser ohne Bearbeitungsrecht. */}
                {z.dokument && (
                    <button
                        type="button"
                        className="vg4-ek-dl"
                        title={`„${z.dokument.fileName}“ herunterladen`}
                        aria-label={`${z.dokument.fileName} herunterladen`}
                        onClick={(e) => {
                            e.stopPropagation();
                            void documentService.openSignedDownload(z.dokument!.id).catch((err) => {
                                console.error("[EreignisListeV4] Download fehlgeschlagen", err);
                                void hinweisen({
                                    titel: "Herunterladen fehlgeschlagen",
                                    text: "Das Dokument konnte nicht heruntergeladen werden.",
                                    ton: "danger",
                                });
                            });
                        }}
                    >
                        <IcoDownload size={15} />
                    </button>
                )}
            </div>
            {/* Der Zweispalter. Er steht auch dann, wenn eine Hälfte leer
                bleibt — sonst rutschte die Karte je nach Kommentarlage mal
                über die halbe, mal über die ganze Breite. */}
            <div className="vg4-ek-rumpf">
                <div className="vg4-ek-halb">
                    {mitVorschau}
                    <Gefaltet z={z} onOeffnen={onOeffneGefaltet} />
                </div>
                {kommentare && ziel ? (
                    <KommentarSpalte
                        kommentare={kommentare.fadenZu(ziel)}
                        users={kommentare.users}
                        titel={`Kommentare zu: ${z.titel}`}
                        auf={offeneFaeden.has(zielSchluessel(ziel))}
                        fadenSchluessel={zielSchluessel(ziel)}
                        onUmschalten={(auf: boolean) => onFadenUmschalten(zielSchluessel(ziel), auf)}
                        {...(onNeu
                            ? {
                                  onNeu: (text: string, m: string[], zusatz?: KommentarZusatz) =>
                                      zusatz ? onNeu(ziel, text, m, undefined, zusatz) : onNeu(ziel, text, m),
                              }
                            : {})}
                        {...(kommentare.aktuellerUserId ? { aktuellerUserId: kommentare.aktuellerUserId } : {})}
                        {...(kommentare.onAendern ? { onAendern: kommentare.onAendern } : {})}
                        {...(kommentare.onLoeschen ? { onLoeschen: kommentare.onLoeschen } : {})}
                        {...(kommentare.onErledigt ? { onErledigt: kommentare.onErledigt } : {})}
                        {...(kommentare.kandidaten ? { kandidaten: kommentare.kandidaten } : {})}
                        {...(kommentare.onBezugOeffnen ? { onBezugOeffnen: kommentare.onBezugOeffnen } : {})}
                        {...(kommentare.mitAnhang ? { mitAnhang: true } : {})}
                    />
                ) : null}
            </div>
        </div>
    );
};

/** „2 Kommentare" — die Pille unter der kompakten Karte; ohne Faden „Kommentieren". */
export function kommentarPillenText(anzahl: number): string {
    if (anzahl === 0) return "Kommentieren";
    return anzahl === 1 ? "1 Kommentar" : `${anzahl} Kommentare`;
}

/**
 * EINE KARTE AUF DEM HANDY (Artifact „Vorgangsdetail mobil", Artboard 1):
 *
 *     (✉) Re: Schadennummer 77-4410             ⋯
 *         Mail · Versicherer · heute 08:52
 *     [💬 2 Kommentare]
 *
 * Antippen öffnet wie am Desktop (Mail-Fenster, Dokument-Betrachter). Das ⋯
 * ruft dasselbe Menü wie der Rechtsklick. Die Kommentarspalte steht nicht
 * daneben (kein Platz), sondern hinter der Pille in einem Sheet — mit Faden,
 * Eingabe und Haken.
 */
const KarteKompakt: React.FC<{
    z: ArchivZeile;
    onOeffnen: (() => void) | undefined;
    onOeffneGefaltet?: (x: { mail?: MailLinkDto; dokument?: DocumentDto }) => void;
    users?: AppUser[];
    onKontextmenue?: (zeile: ArchivZeile, e: React.MouseEvent) => void;
    kommentare?: ZeilenKommentare;
    onEntwurfLoeschen?: (e: VorgangEntwurf) => void;
}> = ({ z, onOeffnen, onOeffneGefaltet, users, onKontextmenue, kommentare, onEntwurfLoeschen }) => {
    const [sheet, setSheet] = useState(false);
    useSheetKlasse(sheet);
    const ziel = kommentare ? zielFuerZeile(z) : null;
    const fadenKey = ziel ? zielSchluessel(ziel) : null;
    // Sprungmarke einer Mitteilung (Plan mobil, PR 8): Der Kommentar steht auf
    // dem Handy im Sheet hinter der Pille — dann geht das Sheet auf, und der
    // Sprung findet den Beitrag dort.
    useEffect(() => {
        if (!fadenKey) return;
        const auf = (e: Event) => {
            if ((e as CustomEvent<string>).detail === fadenKey) setSheet(true);
        };
        window.addEventListener(FADEN_AUF_EREIGNIS, auf);
        return () => window.removeEventListener(FADEN_AUF_EREIGNIS, auf);
    }, [fadenKey]);
    const faden = kommentare && ziel ? kommentare.fadenZu(ziel) : [];
    const onNeu = kommentare?.onNeu;
    const werRoh = z.wer || (z.autorKey ? labelForUserKey(z.autorKey, users ?? []) : "");
    const wer = z.art === "dokument" && werRoh === STEMPEL.dokument ? "" : werRoh;
    const meta = [STEMPEL[z.art], wer, z.wann].filter(Boolean).join(" · ");
    return (
        <div className={`vg4m-ek${z.entwurf ? " entwurf" : ""}`} data-ereignis-key={z.key}>
            <div className="vg4m-ek-zeile">
                <button type="button" className="vg4m-ek-haupt" disabled={!onOeffnen} onClick={onOeffnen}>
                    <span className={`vg4m-ek-ico ${z.art}`} aria-hidden>
                        <ArtSymbol art={z.art} size={18} />
                    </span>
                    <span className="vg4m-ek-text">
                        <span className="vg4m-ek-titel">
                            {z.entwurf && <span className="vg4-entwurf-marke">Entwurf</span>} {z.titel}
                        </span>
                        <span className="vg4m-ek-meta">{meta}</span>
                    </span>
                </button>
                {z.entwurf && z.entwurf.bearbeitbar !== false && onEntwurfLoeschen && (
                    <button
                        type="button"
                        className="vg3-ibtn vg4m-ek-mehr"
                        aria-label={`Entwurf „${z.titel}“ löschen`}
                        onClick={() => onEntwurfLoeschen(z.entwurf!)}
                    >
                        <IcoPapierkorb size={16} />
                    </button>
                )}
                {onKontextmenue && (
                    <button
                        type="button"
                        className="vg3-ibtn vg4m-ek-mehr"
                        aria-label={`Weitere Aktionen: ${z.titel}`}
                        title="Weitere Aktionen"
                        onClick={(e) => onKontextmenue(z, e)}
                    >
                        <IcoMehrWaagerecht size={16} />
                    </button>
                )}
            </div>
            <Gefaltet z={z} onOeffnen={onOeffneGefaltet} />
            {kommentare && ziel && (faden.length > 0 || onNeu) ? (
                <button
                    type="button"
                    className={`vg4m-ek-pille${faden.length ? " mit" : ""}`}
                    data-faden={zielSchluessel(ziel)}
                    onClick={() => setSheet(true)}
                >
                    <IcoSprechblase size={14} />
                    {kommentarPillenText(faden.length)}
                </button>
            ) : null}
            {kommentare && ziel && (
                <BottomSheet open={sheet} title="Kommentare" onClose={() => setSheet(false)}>
                    <div className="vg4m-ek-sheet-titel">{z.titel}</div>
                    <KommentarSpalte
                        kommentare={faden}
                        users={kommentare.users}
                        titel={`Kommentare zu: ${z.titel}`}
                        auf
                        fadenSchluessel={zielSchluessel(ziel)}
                        onUmschalten={() => undefined}
                        {...(onNeu
                            ? {
                                  onNeu: (text: string, m: string[], zusatz?: KommentarZusatz) =>
                                      zusatz ? onNeu(ziel, text, m, undefined, zusatz) : onNeu(ziel, text, m),
                              }
                            : {})}
                        {...(kommentare.aktuellerUserId ? { aktuellerUserId: kommentare.aktuellerUserId } : {})}
                        {...(kommentare.onAendern ? { onAendern: kommentare.onAendern } : {})}
                        {...(kommentare.onLoeschen ? { onLoeschen: kommentare.onLoeschen } : {})}
                        {...(kommentare.onErledigt ? { onErledigt: kommentare.onErledigt } : {})}
                        {...(kommentare.kandidaten ? { kandidaten: kommentare.kandidaten } : {})}
                        {...(kommentare.onBezugOeffnen ? { onBezugOeffnen: kommentare.onBezugOeffnen } : {})}
                        {...(kommentare.mitAnhang ? { mitAnhang: true } : {})}
                    />
                </BottomSheet>
            )}
        </div>
    );
};

/**
 * Was in der Karte zusammengefaltet steht (Rücksprache 06.10.2026): die
 * übrigen Mails der Unterhaltung bzw. die früheren Fassungen der Datei.
 * Zugeklappt nur eine Zeile, damit die Karte so ruhig bleibt wie eine
 * einzelne; aufgeklappt je Stück eine Zeile zum Öffnen.
 */
const Gefaltet: React.FC<{
    z: ArchivZeile;
    onOeffnen?: (x: { mail?: MailLinkDto; dokument?: DocumentDto }) => void;
}> = ({ z, onOeffnen }) => {
    const [auf, setAuf] = useState(false);
    const mails = z.mail ? (z.unterhaltung ?? []) : [];
    const fassungen = z.dokument ? (z.fassungen ?? []) : [];
    const n = mails.length + fassungen.length;
    if (n === 0) return null;
    const label =
        mails.length > 0
            ? mails.length === 1
                ? "1 weitere Mail der Unterhaltung"
                : `${mails.length} weitere Mails der Unterhaltung`
            : fassungen.length === 1
              ? "1 frühere Fassung"
              : `${fassungen.length} frühere Fassungen`;
    return (
        <div className="vg4-ek-gefaltet">
            <button
                type="button"
                className="vg4-ek-gefaltet-kopf"
                aria-expanded={auf}
                onClick={(e) => {
                    e.stopPropagation();
                    setAuf(!auf);
                }}
            >
                <span aria-hidden>{auf ? "▾" : "▸"}</span>
                {label}
            </button>
            {auf && (
                <ul className="vg4-ek-gefaltet-liste">
                    {mails.map((m) => (
                        <li key={`m${m.id}`}>
                            <button
                                type="button"
                                disabled={!onOeffnen}
                                title={m.subject ? `${m.subject} — zum Anhängen in ein Mailfenster ziehen` : undefined}
                                {...ziehbarAls({ mail: m })}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOeffnen?.({ mail: m });
                                }}
                            >
                                <span className="vg4-ek-gefaltet-wer">{m.senderName || m.senderEmail || "Unbekannt"}</span>
                                <span className="vg4-ek-gefaltet-text">{m.preview || m.subject || ""}</span>
                                <span className="vg4-ek-gefaltet-wann">{gefaltetDatum(m.receivedAt ?? m.linkedAt)}</span>
                            </button>
                        </li>
                    ))}
                    {fassungen.map((d) => (
                        <li key={`d${d.id}`}>
                            <button
                                type="button"
                                disabled={!onOeffnen}
                                title={`„${d.fileName}“ öffnen — zum Anhängen in ein Mailfenster ziehen`}
                                {...ziehbarAls({ dokument: d })}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onOeffnen?.({ dokument: d });
                                }}
                            >
                                <DateiTypPlakette fileName={d.fileName} mimeType={d.mimeType} />
                                <span className="vg4-ek-gefaltet-text">{d.fileName}</span>
                                <span className="vg4-ek-gefaltet-wann">{gefaltetDatum(d.uploadedAt)}</span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

/**
 * Drag-Props für eine Mail oder ein Dokument aus dem Vorgang — Ziel ist das
 * Verfassen-Fenster (`ComposeMailWindow`), das beide als Anhang annimmt.
 * Nur KMVS-eigene MIME-Typen: die Datei-Ablagen des Vorgangs reagieren nur auf
 * echte Dateien und laden also nichts ein zweites Mal hoch.
 */
export function ziehbarAls(
    was: { mail: MailLinkDto } | { dokument: DocumentDto } | null,
): { draggable?: true; onDragStart?: (e: React.DragEvent) => void } {
    if (!was) return {};
    return {
        draggable: true,
        onDragStart: (e: React.DragEvent) => {
            if ("mail" in was) {
                const zug = mailZugAusLink(was.mail);
                e.dataTransfer.setData(ZIEHTYP_MAIL, JSON.stringify(zug));
                // Zusätzlich als Postfach-Mail: So lässt sie sich auch in die
                // Dokumente eines Kontakts ziehen (User 01.11.2026). Die
                // Vorgangskarten sehen zuerst `ZIEHTYP_MAIL` und verknüpfen
                // nicht ein zweites Mal (`dropArt`).
                const { linkId: _ohne, ...postfach } = zug;
                void _ohne;
                e.dataTransfer.setData(ZIEHTYP_POSTFACH_MAIL, JSON.stringify(postfach));
            } else e.dataTransfer.setData(ZIEHTYP_DOKUMENT, JSON.stringify(dokumentZugAus(was.dokument)));
            e.dataTransfer.effectAllowed = "copy";
        },
    };
}

function gefaltetDatum(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** „Klaus Baltes" wird zu „KB" — das Zeichen im Kreis der Mailkarte. */
function initialen(name: string): string {
    const teile = String(name).split(/\s+/).filter(Boolean).slice(0, 2);
    return teile.map((w) => w[0]!.toUpperCase()).join("") || "?";
}

/**
 * Das Zeichen einer Art — im Kreis der Karte (wo es keinen Absender gibt), im
 * Stempel und an den Filter-Chips (PO 28.09.2026: „Anruf-Chip mit
 * Telefon-Icon, generell die Icons mit anzeigen"). Eine Zuordnung für alle
 * drei Stellen, damit der Anruf nicht hier ein Telefon und dort etwas
 * anderes trägt.
 */
export const ArtSymbol: React.FC<{ art: ArchivArt; size?: number }> = ({ art, size = 16 }) =>
    art === "anruf" ? (
        <IcoAnruf size={size} />
    ) : art === "mail" ? (
        <IcoMail size={size} />
    ) : art === "termin" ? (
        <IcoKalender size={size} />
    ) : art === "dokument" ? (
        <IcoDokument size={size} />
    ) : art === "kommentar" ? (
        <IcoSprechblase size={size} />
    ) : (
        <IcoNotiz size={size} />
    );
